import type {
  CheckResult,
  Contact,
  DocumentFields,
  DocumentKind,
  LineItemInput,
  TaxRate,
} from './types.js';
import { FORMS_WITH_LINES } from './payloads.js';
import { hasArabicIndicDigits } from './normalize.js';
import { message } from './messages.js';

export interface ValidationContext {
  contacts: Contact[];
  taxRates: TaxRate[];
  /**
   * References already present in Xero for this organisation, from
   * GET /Invoices?where=Reference. Empty when the duplicate search has not run.
   */
  knownReferences: string[];
}

/**
 * Pre-push checks, run before anything is sent to Xero.
 *
 * A failing check blocks the push; a warning (`warn`) lets it through but is
 * surfaced first, because a duplicate reference is legitimate often enough that
 * refusing outright would be wrong.
 *
 * Nothing here is translated. Labels come back as fixed phrases the interface
 * looks up, and details come back as a code plus its values, so Arabic can put
 * the number where Arabic puts numbers. See messages.ts.
 */
export function validateDocument(
  kind: DocumentKind,
  fields: DocumentFields,
  lines: LineItemInput[],
  ctx: ValidationContext,
): CheckResult[] {
  const checks: CheckResult[] = [];

  const contactId = typeof fields.contactId === 'string' ? fields.contactId : '';
  const contact = ctx.contacts.find((c) => c.id === contactId);

  if (kind === 'contact' || kind === 'item') {
    const name = typeof fields.name === 'string' ? fields.name.trim() : '';
    const code = typeof fields.code === 'string' ? fields.code.trim() : '';
    if (kind === 'contact') {
      checks.push({
        label: 'Contact name is present and unique',
        detail: name
          ? message('contact_resolved', { name })
          : message('no_contact_selected'),
        ok: name.length > 0,
      });
    } else {
      checks.push({
        label: 'Item code is present',
        detail: code
          ? message('contact_resolved', { name: code })
          : message('no_contact_selected'),
        ok: code.length > 0,
      });
    }
  } else {
    checks.push({
      label: 'Contact resolved to a ContactID',
      detail: contact
        ? message('contact_resolved', { name: contact.name })
        : message('no_contact_selected'),
      ok: Boolean(contact),
    });
  }

  if (FORMS_WITH_LINES.has(kind)) {
    const noDesc = lines.filter((l) => !String(l.description ?? '').trim()).length;
    const missingAccount = lines
      .map((line, index) => ({ line, index }))
      .filter(({ line }) => !line.accountCode);
    const badTax = lines.filter(
      (l) => l.taxType && !ctx.taxRates.some((r) => r.type === l.taxType),
    ).length;

    checks.push({
      label: 'Every line has a Description',
      detail: noDesc
        ? message('lines_missing_description', { count: noDesc })
        : message('line_count', { count: lines.length }),
      ok: noDesc === 0,
    });

    checks.push({
      label: 'Every line has an AccountCode',
      detail:
        missingAccount.length === 1
          ? // One offender is worth naming; several are better counted.
            message('line_missing_account', { line: missingAccount[0]!.index + 1 })
          : missingAccount.length > 1
            ? message('lines_missing_account', { count: missingAccount.length })
            : message('from_chart_of_accounts'),
      ok: missingAccount.length === 0,
    });

    checks.push({
      label: 'Tax types valid for the org',
      detail: badTax
        ? message('lines_invalid_tax', { count: badTax })
        : message('tax_rates_cached', { count: ctx.taxRates.length }),
      ok: badTax === 0,
    });
  }

  const date = typeof fields.date === 'string' ? fields.date : '';
  checks.push({
    label: 'Dates normalised to YYYY-MM-DD',
    // A date is already digits and separators, so it needs no template.
    detail: message('contact_resolved', { name: date || '—' }),
    ok: /^\d{4}-\d{2}-\d{2}$/.test(date),
  });

  const typedValues = [...lines.map((l) => `${l.qty}${l.price}${l.disc}`), date].join('');
  checks.push({
    label: 'Arabic-Indic digits converted to Western',
    detail: hasArabicIndicDigits(typedValues)
      ? message('arabic_digits_folded')
      : message('digits_normalised'),
    ok: true,
  });

  const reference = typeof fields.reference === 'string' ? fields.reference.trim() : '';
  const duplicate = reference !== '' && ctx.knownReferences.includes(reference);
  checks.push({
    label: 'Duplicate check against Xero',
    detail: duplicate
      ? message('duplicate_reference', { reference })
      : reference
        ? message('no_match_for_reference', { reference })
        : message('no_reference_entered'),
    ok: !duplicate,
    warn: duplicate,
  });

  return checks;
}

export interface CheckSummary {
  blocking: number;
  warnings: number;
  canPush: boolean;
}

export function summariseChecks(checks: CheckResult[]): CheckSummary {
  const blocking = checks.filter((c) => !c.ok && !c.warn).length;
  const warnings = checks.filter((c) => c.warn).length;
  return { blocking, warnings, canPush: blocking === 0 };
}
